<template>
  <ListPage variant="card" class="tw-p-4 sm:tw-p-6" v-model:pageSize="pageSize" v-slot="{ pageSizeOptions, setPageSize }">
    <div class="tw-mb-4 tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
      <div class="tw-text-left">
        <h3 class="tw-text-lg tw-font-semibold tw-text-slate-900">Vessels</h3>
        <div class="tw-mt-1 tw-text-sm tw-text-slate-600">Manage vessel names used in orders.</div>
      </div>

      <button
        type="button"
        class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800"
        @click="openCreate"
      >
        Add vessel
      </button>
    </div>

    <div class="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-text-sm tw-text-slate-600">
      <input
        v-model="searchQuery"
        type="text"
        placeholder="Search vessel name..."
        class="tw-h-10 tw-w-full sm:tw-w-80 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/30"
      />
      <div class="tw-flex tw-items-center tw-gap-2">
        <span>Rows</span>
        <select
          class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-px-2 tw-py-1 tw-text-sm tw-text-slate-800"
          :value="String(pageSize)"
          @change="(e) => setPageSize(Number((e.target as HTMLSelectElement).value))"
        >
          <option v-for="opt in pageSizeOptions" :key="opt" :value="String(opt)">{{ opt }}</option>
        </select>
      </div>
    </div>

    <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-p-3 sm:tw-p-4">
      <div class="tw-overflow-x-auto">
        <table class="tw-min-w-full tw-border-separate tw-border-spacing-0 tw-text-left">
          <thead>
            <tr class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500">
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-px-3 tw-py-3">Name</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-px-3 tw-py-3">MMSI</th>
              <th class="tw-sticky tw-top-0 tw-bg-white/80 tw-px-3 tw-py-3"></th>
            </tr>
          </thead>
          <tbody class="tw-divide-y tw-divide-slate-200/70">
            <tr
              v-for="vessel in vessels"
              :key="vessel.id"
              class="tw-cursor-pointer hover:tw-bg-slate-50/70"
              @click="openEdit(vessel)"
            >
              <td class="tw-px-3 tw-py-3 tw-text-sm tw-font-semibold tw-text-slate-900">{{ vessel.name }}</td>
              <td class="tw-px-3 tw-py-3 tw-text-sm tw-text-slate-700">{{ vessel.mssi || '—' }}</td>
              <td class="tw-px-3 tw-py-3 tw-text-right">
                <button
                  type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-800 hover:tw-bg-white"
                  @click.stop="openEdit(vessel)"
                >
                  Edit
                </button>
              </td>
            </tr>
            <tr v-if="!loading && vessels.length === 0">
              <td colspan="3" class="tw-px-3 tw-py-6 tw-text-center tw-text-sm tw-text-slate-500">
                No vessels found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="tw-mt-4 tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-slate-600">
      <span>Page {{ page + 1 }} of {{ totalPages }}</span>
      <span>Showing {{ vessels.length }} of {{ totalCount }} vessels</span>
    </div>

    <div class="tw-mt-4 tw-flex tw-justify-center" v-if="totalPages > 1">
      <PaginationBar
        :page="page"
        :totalPages="totalPages"
        @previousPage="prevPage"
        @nextPage="nextPage"
        @goToPage="goToPage"
      />
    </div>

    <EditVessel v-model:isOpen="isModalOpen" :vessel="selectedVessel" @close="closeModal" @saved="refresh" />
  </ListPage>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useQuery } from '@vue/apollo-composable';

import VesselsPaginatedQuery from '@/gql/orders/VesselsPaginated.gql';
import EditVessel from '@/components/Vessel/EditVessel.vue';
import PaginationBar from '@/components/core/PageElements/PaginationBar.vue';
import ListPage from '@/components/core/ListPage.vue';

const isModalOpen = ref(false);
const selectedVessel = ref<any | null>(null);
const page = ref(0);
const pageSize = ref(10);
const searchQuery = ref('');

const queryVariables = computed(() => ({
  pageNum: page.value,
  pageSize: pageSize.value,
  searchQuery: searchQuery.value.trim() || null,
}));

const { result, loading, refetch } = useQuery(VesselsPaginatedQuery, queryVariables);
const vessels = computed(() => result.value?.vesselsPaginated || []);
const totalCount = computed(() => Number(result.value?.vesselsCount || 0));
const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / pageSize.value)));

watch(pageSize, () => {
  page.value = 0;
});

watch(searchQuery, () => {
  page.value = 0;
});

watch([totalCount, loading], ([count, isLoading]) => {
  // Do not clamp while a new page request is in-flight, otherwise we can
  // reset page back to 0 and cancel the just-triggered request.
  if (isLoading) return;
  if (!count) {
    page.value = 0;
    return;
  }
  const maxPage = Math.max(0, Math.ceil(count / pageSize.value) - 1);
  if (page.value > maxPage) {
    page.value = maxPage;
  }
});

function openCreate() {
  selectedVessel.value = null;
  isModalOpen.value = true;
}

function openEdit(vessel: any) {
  selectedVessel.value = vessel;
  isModalOpen.value = true;
}

function closeModal() {
  isModalOpen.value = false;
}

async function refresh() {
  await refetch(queryVariables.value);
}

function prevPage() {
  if (page.value > 0) page.value -= 1;
}

function nextPage() {
  if (page.value + 1 < totalPages.value) page.value += 1;
}

function goToPage(nextPage: number) {
  if (nextPage < 0 || nextPage >= totalPages.value) return;
  page.value = nextPage;
}
</script>
